import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Sparkles, Mail, Lock, User, ArrowRight, Globe, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

export const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(language === 'fr' ? 'Connexion réussie !' : 'Login successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Identifiants invalides' : 'Invalid credentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-sm">
        {/* Logo centré */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#FFE66D] flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="font-serif text-2xl font-semibold text-stone-800">Ikigai</span>
          </Link>
        </div>

        {/* Card formulaire */}
        <div className="bg-white rounded-3xl p-6 shadow-soft border border-stone-100">
          {/* Language toggle en haut à droite */}
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setLanguage(language === 'fr' ? 'en' : 'fr')}
              className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-700 transition-colors bg-stone-50 px-3 py-1.5 rounded-full"
              data-testid="auth-language-toggle"
            >
              <Globe className="w-4 h-4" />
              {language === 'fr' ? 'EN' : 'FR'}
            </button>
          </div>

          <div className="text-center mb-6">
            <h1 className="font-serif text-2xl font-semibold text-stone-900 mb-1">
              {language === 'fr' ? 'Bon retour' : 'Welcome back'}
            </h1>
            <p className="text-stone-500 text-sm">
              {language === 'fr' ? 'Continue ton voyage Ikigai' : 'Continue your Ikigai journey'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-stone-700 text-sm">{t('email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ton@email.com"
                  className="input-ikigai pl-10 w-full h-11 text-sm"
                  required
                  data-testid="login-email"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-stone-700 text-sm">{t('password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-ikigai pl-10 pr-10 w-full h-11 text-sm"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors"
                  data-testid="login-toggle-password"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="text-right">
                <Link 
                  to="/forgot-password" 
                  className="text-xs text-stone-500 hover:text-[#FF6B6B] transition-colors"
                  data-testid="forgot-password-link"
                >
                  {language === 'fr' ? 'Mot de passe oublié ?' : 'Forgot password?'}
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#FF6B6B] hover:bg-[#EE5253] text-white rounded-xl h-11 font-medium flex items-center justify-center gap-2 mt-6"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <span className="animate-pulse">{t('loading')}</span>
              ) : (
                <>
                  {t('login')}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-stone-500 text-sm">
            {t('noAccount')}{' '}
            <Link to="/register" className="text-[#FF6B6B] font-medium hover:underline" data-testid="go-to-register">
              {t('register')}
            </Link>
          </p>
        </div>

        {/* Mini Ikigai circles decoration */}
        <div className="flex justify-center gap-2 mt-6">
          <div className="w-3 h-3 rounded-full bg-[#FF6B6B]/30" />
          <div className="w-3 h-3 rounded-full bg-[#FFE66D]/30" />
          <div className="w-3 h-3 rounded-full bg-[#4ECDC4]/30" />
          <div className="w-3 h-3 rounded-full bg-[#1A535C]/30" />
        </div>
      </div>
    </div>
  );
};

export const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password, name, language);
      toast.success(language === 'fr' ? 'Compte créé avec succès !' : 'Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de l\'inscription' : 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-sm">
        {/* Logo centré */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#FFE66D] flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="font-serif text-2xl font-semibold text-stone-800">Ikigai</span>
          </Link>
        </div>

        {/* Card formulaire */}
        <div className="bg-white rounded-3xl p-6 shadow-soft border border-stone-100">
          {/* Language toggle en haut à droite */}
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setLanguage(language === 'fr' ? 'en' : 'fr')}
              className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-700 transition-colors bg-stone-50 px-3 py-1.5 rounded-full"
              data-testid="register-language-toggle"
            >
              <Globe className="w-4 h-4" />
              {language === 'fr' ? 'EN' : 'FR'}
            </button>
          </div>

          <div className="text-center mb-6">
            <h1 className="font-serif text-2xl font-semibold text-stone-900 mb-1">
              {language === 'fr' ? 'Commence ton voyage' : 'Start your journey'}
            </h1>
            <p className="text-stone-500 text-sm">
              {language === 'fr' ? 'Découvre ton Ikigai en 30 jours' : 'Discover your Ikigai in 30 days'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-stone-700 text-sm">{t('name')}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={language === 'fr' ? 'Ton prénom' : 'Your first name'}
                  className="input-ikigai pl-10 w-full h-11 text-sm"
                  required
                  data-testid="register-name"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-stone-700 text-sm">{t('email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ton@email.com"
                  className="input-ikigai pl-10 w-full h-11 text-sm"
                  required
                  data-testid="register-email"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-stone-700 text-sm">{t('password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-ikigai pl-10 pr-10 w-full h-11 text-sm"
                  minLength={6}
                  required
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors"
                  data-testid="register-toggle-password"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#FF6B6B] hover:bg-[#EE5253] text-white rounded-xl h-11 font-medium flex items-center justify-center gap-2 mt-6"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? (
                <span className="animate-pulse">{t('loading')}</span>
              ) : (
                <>
                  {t('createAccount')}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-stone-500 text-sm">
            {t('hasAccount')}{' '}
            <Link to="/login" className="text-[#FF6B6B] font-medium hover:underline" data-testid="go-to-login">
              {t('login')}
            </Link>
          </p>
        </div>

        {/* Mini Ikigai circles decoration */}
        <div className="flex justify-center gap-2 mt-6">
          <div className="w-3 h-3 rounded-full bg-[#FF6B6B]/30" />
          <div className="w-3 h-3 rounded-full bg-[#FFE66D]/30" />
          <div className="w-3 h-3 rounded-full bg-[#4ECDC4]/30" />
          <div className="w-3 h-3 rounded-full bg-[#1A535C]/30" />
        </div>
      </div>
    </div>
  );
};
